import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BellRinging, Plus, X, ArrowClockwise, Trash, MapPin, CurrencyDollar,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * LoadBoardWatchersCard — pilot-facing rules that fire an email the moment
 * a new load board post matches a watched broker + lane. Sits on the load
 * board page above the search form so pilots see their active guardrails
 * before running a fresh scan.
 */
export default function LoadBoardWatchersCard() {
  const [watchers, setWatchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/loadboard-watchers");
      setWatchers(data.watchers || []);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this watcher?")) return;
    try {
      await client.delete(`/loadboard-watchers/${id}`);
      toast.success("Watcher removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await client.post("/loadboard-watchers/scan-now", {});
      if (data.fired > 0) toast.success(`${data.fired} watcher${data.fired !== 1 ? "s" : ""} fired notifications`);
      else toast.info(data.reason || "No matches on the current board");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const toggleActive = async (w) => {
    try {
      await client.patch(`/loadboard-watchers/${w.id}`, { is_active: !w.is_active });
      load();
    } catch { toast.error("Toggle failed"); }
  };

  if (loading) return null;

  return (
    <>
      <section
        className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
        data-testid="watchers-card"
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#FF5A1F] flex items-center gap-1.5">
              <BellRinging size={12} weight="fill" /> Live watchers
            </div>
            <h2 className="mt-1 text-xl font-bold text-white">Broker + lane alerts</h2>
            <div className="mt-1 text-xs text-white/50 max-w-lg">
              Pin brokers you've won with. When a matching load posts on the board, we email you
              before other carriers pile on.
            </div>
          </div>
          <div className="flex items-center gap-2">
            {watchers.length > 0 && (
              <button
                onClick={scanNow} disabled={scanning}
                data-testid="watchers-scan-now"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/70 hover:border-white/30 hover:text-white disabled:opacity-50"
              >
                <ArrowClockwise size={11} className={scanning ? "animate-spin" : ""} />
                {scanning ? "Scanning" : "Scan now"}
              </button>
            )}
            <button
              onClick={() => setModalOpen(true)}
              data-testid="watchers-new"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#FF5A1F] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#FF5A1F]/90"
            >
              <Plus size={11} weight="bold" /> New watcher
            </button>
          </div>
        </div>

        {watchers.length === 0 ? (
          <div className="mt-5 rounded-lg border border-dashed border-white/10 p-6 text-center">
            <BellRinging size={20} className="mx-auto mb-2 text-white/40" />
            <div className="text-sm text-white/60">No watchers set up.</div>
            <div className="text-xs text-white/40 mt-1">
              Add one for your favorite broker + lane and we'll ping you when they post.
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {watchers.map((w) => (
              <div
                key={w.id}
                className={`rounded-lg border p-3 ${w.is_active ? "border-white/10 bg-white/[0.02]" : "border-white/5 bg-white/[0.01] opacity-60"}`}
                data-testid={`watcher-row-${w.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-white truncate">
                      {w.label || w.broker}
                    </div>
                    <div className="text-[11px] text-white/50 truncate">
                      Broker: <b className="text-white/70">{w.broker}</b>
                    </div>
                  </div>
                  <button
                    onClick={() => del(w.id)}
                    className="p-1 text-white/40 hover:text-red-400"
                    data-testid={`watcher-delete-${w.id}`}
                  >
                    <Trash size={12} />
                  </button>
                </div>
                <div className="mt-2 flex items-center flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 text-[10px] text-white/50 border border-white/10 rounded-full px-2 py-0.5">
                    <MapPin size={10} /> {w.origin_state || "ANY"} → {w.destination_state || "ANY"}
                  </span>
                  {w.min_rate_per_mile ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300 border border-emerald-500/30 rounded-full px-2 py-0.5">
                      <CurrencyDollar size={10} /> ≥ ${w.min_rate_per_mile}/mi
                    </span>
                  ) : null}
                  <button
                    onClick={() => toggleActive(w)}
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${
                      w.is_active
                        ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"
                        : "border-white/10 text-white/40"
                    }`}
                    data-testid={`watcher-toggle-${w.id}`}
                  >
                    {w.is_active ? "ACTIVE" : "PAUSED"}
                  </button>
                </div>
                <div className="mt-2 text-[10px] text-white/40">
                  {w.match_count > 0
                    ? `${w.match_count} match${w.match_count !== 1 ? "es" : ""} fired`
                    : "no matches yet"}
                  {w.last_matched_at && (
                    <span className="ml-1">
                      · last {new Date(w.last_matched_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      {modalOpen && (
        <NewWatcherModal onClose={() => setModalOpen(false)} onCreated={load} />
      )}
    </>
  );
}

function NewWatcherModal({ onClose, onCreated }) {
  const [broker, setBroker] = useState("");
  const [origin, setOrigin] = useState("");
  const [dest, setDest] = useState("");
  const [floor, setFloor] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!broker.trim()) { toast.error("Broker name is required"); return; }
    setBusy(true);
    try {
      await client.post("/loadboard-watchers", {
        broker: broker.trim(),
        origin_state: origin.trim() || undefined,
        destination_state: dest.trim() || undefined,
        min_rate_per_mile: floor ? Number(floor) : undefined,
      });
      toast.success("Watcher created — we'll ping you the moment it fires");
      onCreated && onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="watcher-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#FF5A1F]">
              New watcher
            </div>
            <h2 className="mt-1 text-xl font-bold text-white">Alert me when this posts</h2>
          </div>
          <button onClick={onClose} className="p-1.5 text-white/50 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <Field label="Broker name" testid="watcher-broker">
            <input
              value={broker} onChange={(e) => setBroker(e.target.value)}
              placeholder="e.g. Landstar, TQL, J.B. Hunt"
              className="input-dark" data-testid="watcher-broker-input" required
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Origin state (optional)">
              <input
                value={origin} onChange={(e) => setOrigin(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="TX" maxLength={2}
                className="input-dark" data-testid="watcher-origin-input"
              />
            </Field>
            <Field label="Dest state (optional)">
              <input
                value={dest} onChange={(e) => setDest(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="CA" maxLength={2}
                className="input-dark" data-testid="watcher-dest-input"
              />
            </Field>
          </div>
          <Field label="Min rate/mile (optional)">
            <input
              type="number" step="0.01" value={floor}
              onChange={(e) => setFloor(e.target.value)}
              placeholder="2.50"
              className="input-dark" data-testid="watcher-floor-input"
            />
          </Field>
          <div className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] text-white/50">
            Notifications go to your account email. Leave state fields blank to catch every lane
            for this broker.
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button" onClick={onClose} disabled={busy}
              className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/70 hover:bg-white/5"
            >Cancel</button>
            <button
              type="submit" disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg bg-[#FF5A1F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#FF5A1F]/90 disabled:opacity-50"
              data-testid="watcher-submit"
            >
              <BellRinging size={14} weight="fill" />
              {busy ? "Saving…" : "Create watcher"}
            </button>
          </div>
        </form>
      </div>
      <style>{`
        .input-dark {
          width: 100%; border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(0,0,0,0.4); padding: 8px 12px;
          font-size: 13px; color: white; outline: none;
        }
        .input-dark:focus { border-color: #FF5A1F; }
      `}</style>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="block text-xs font-semibold uppercase tracking-wider text-white/60 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
